import { HttpErrorResponse } from '@angular/common/http';
import { isApiBusinessError } from '../errors/api-business.error';

/**
 * A string body is either JSON to parse, or already the message. Returns the parsed
 * object, or a string to use verbatim.
 */
function normalizeStringBody(body: string): { text: string } | { parsed: unknown } {
  const trimmed = body.trim();
  if (!trimmed) {
    return { text: '' };
  }
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return { text: trimmed };
  }
  try {
    return { parsed: JSON.parse(trimmed) };
  } catch {
    return { text: trimmed };
  }
}

/** Reads the `message` field in any of the shapes the API returns it. */
function readMessageField(body: Record<string, unknown>): string {
  const m = body['message'];
  if (typeof m === 'string') {
    return m.trim();
  }
  if (Array.isArray(m)) {
    return m.map(String).join(' ').trim();
  }
  // ValidationExceptionFilter returns `message` keyed by field —
  // { code: ['code must be 2–16 uppercase letters…'] }. Without this the caller
  // falls back to Angular's generic "Http failure response … 422" and the operator
  // never learns which field was rejected.
  if (typeof m === 'object' && m !== null) {
    return formatFieldErrors(m as Record<string, unknown>);
  }
  return '';
}

/**
 * Reads `message` from Angular `HttpErrorResponse` bodies (JSON object or JSON **string**).
 * Handles Nest `AppExceptionsFilter` shape `{ statusCode, status, message, ... }`.
 */
export function extractMessageFromHttpError(err: unknown): string {
  if (!(err instanceof HttpErrorResponse)) {
    return '';
  }

  let body: unknown = err.error;
  if (body instanceof Blob) {
    return '';
  }

  if (typeof body === 'string') {
    const normalized = normalizeStringBody(body);
    if ('text' in normalized) {
      return normalized.text;
    }
    body = normalized.parsed;
  }

  if (body == null || typeof body !== 'object') {
    return '';
  }
  const record = body as Record<string, unknown>;
  const headline = readMessageField(record);
  const detail = readStructuredErrors(record);
  if (!detail) {
    return headline;
  }
  // "Workflow definition is invalid" on its own tells an operator nothing they can act on.
  // The API already sends what is wrong; without this it was dropped on the floor.
  return headline ? `${headline}: ${detail}` : detail;
}

/**
 * `errors: [{ code, message }]` — the structured detail AppExceptionsFilter preserves for
 * failures that have more to say than one sentence, e.g. flow validation.
 */
function readStructuredErrors(body: Record<string, unknown>): string {
  const errors = body['errors'];
  if (!Array.isArray(errors)) {
    return '';
  }
  return errors
    .map((e) => {
      if (typeof e === 'string') return e.trim();
      if (e && typeof e === 'object') {
        const message = (e as Record<string, unknown>)['message'];
        if (typeof message === 'string') return message.trim();
      }
      return '';
    })
    .filter(Boolean)
    .join('; ')
    .trim();
}

/** "field: message" per entry, so a form error names the field it belongs to. */
function formatFieldErrors(errors: Record<string, unknown>): string {
  return Object.entries(errors)
    .map(([field, messages]) => {
      const text = Array.isArray(messages) ? messages.map(String).join(', ') : String(messages);
      return text.trim() ? `${field}: ${text.trim()}` : '';
    })
    .filter(Boolean)
    .join('; ')
    .trim();
}

/** Resolves a human-readable API message from any thrown client error shape. */
export function resolveApiErrorMessage(err: unknown): string {
  if (isApiBusinessError(err)) {
    return err.message?.trim() || '';
  }
  const fromBody = extractMessageFromHttpError(err);
  if (fromBody) {
    return fromBody;
  }
  if (err instanceof HttpErrorResponse) {
    return (err.message || '').trim();
  }
  if (err instanceof Error) {
    return (err.message || '').trim();
  }
  return '';
}
